import { useEffect, useId, useRef, useState } from "react";
import { IonCheckbox, IonModal, IonSpinner } from "@ionic/react";
import { Card, Colors, ErrorAlert } from "swing-components";

import { translate } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";
import { useLanguages } from "./languageHooks";

type Language = {
  readonly key: string;
  readonly order: number;
  readonly label: string;
  readonly value: string;
};

type LanguageFieldProps = {
  languageValues: string[];
  onChange: (languages: Array<string>) => void;
  isDisabled?: boolean;
};

export function LanguageField(props: LanguageFieldProps) {
  const { languageValues = [], onChange, isDisabled } = props;

  const { languagesData } = useLanguages(languageValues);

  const modalId = useId();
  const languageModal = useRef<HTMLIonModalElement>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [tempLanguageValues, setTempLanguageValues] = useState<string[]>([]);
  const [otherLanguageText, setOtherLanguageText] = useState("");
  const [showOtherError, setShowOtherError] = useState(false);

  useEffect(() => {
    if (languagesData?.length > 0) {
      const predefinedLabels = languagesData.map((l: Language) => l.label);
      // Compute checkbox state directly from languageValues
      const checkboxState = languageValues.filter((lang) => predefinedLabels.includes(lang));
      const customLang = languageValues.find((lang) => !predefinedLabels.includes(lang));
      if (customLang) {
        checkboxState.push("Other");
      }
      setTempLanguageValues(checkboxState);
      setOtherLanguageText(customLang || "");
    }
  }, [languagesData, languageValues]);

  const filteredLanguages = languagesData
    ?.filter((language: Language) =>
      language.label.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .toSorted((a, b) => a.order - b.order);

  function handleModalClose() {
    setSearchTerm("");
    setShowOtherError(false);
    // Reset to original values based on languageValues prop
    const predefinedLabels = languagesData?.map((l: Language) => l.label) || [];
    const checkboxState = languageValues.filter((lang) => predefinedLabels.includes(lang));
    const customLang = languageValues.find((lang) => !predefinedLabels.includes(lang));
    if (customLang) {
      checkboxState.push("Other");
    }
    setTempLanguageValues(checkboxState);
    setOtherLanguageText(customLang || "");
    languageModal.current?.dismiss();
  }

  function handleModalConfirm() {
    // Validate: if "Other" is selected, the text field must be filled
    if (tempLanguageValues.includes("Other") && !otherLanguageText.trim()) {
      // Show error and don't close the modal
      setShowOtherError(true);
      return;
    }

    setSearchTerm("");
    setShowOtherError(false);
    // If "Other" is selected, replace it with the custom text (without prefix)
    const finalValues = tempLanguageValues.map((lang) => {
      if (lang === "Other" && otherLanguageText.trim()) {
        return otherLanguageText.trim();
      }
      return lang;
    });
    onChange(finalValues);
    languageModal.current?.dismiss();
  }

  function handleChange(params: { checked: boolean; value: string }) {
    const { checked, value } = params;

    if (checked) {
      setTempLanguageValues((tempLanguageValues) => [...tempLanguageValues, value]);
    } else {
      setTempLanguageValues(tempLanguageValues.filter((item) => item !== value));
      // Clear the other language text if "Other" is unchecked
      if (value === "Other") {
        setOtherLanguageText("");
      }
    }
  }

  /**
   * Renders the "Other" language option with its text input field
   */
  function renderOtherOption(language: Language, showDivider: boolean = false) {
    const isOtherChecked = tempLanguageValues.includes("Other");

    return (
      <div
        css={{
          boxShadow: showDivider ? `inset 0 -1px 0 ${Colors.slate200}` : "none",
        }}
      >
        <IonCheckbox
          labelPlacement="end"
          justify="start"
          value={language.value}
          checked={tempLanguageValues.includes(language.value)}
          onIonChange={(e) => handleChange(e.detail)}
          css={{
            width: "100%",
            padding: 16,
          }}
        >
          {language.label}
        </IonCheckbox>

        {/* Show input field inline when Other is checked */}
        {isOtherChecked && (
          <div
            css={{
              paddingLeft: 56, // Align with checkbox text
              paddingRight: 16,
              paddingBottom: 16,
              paddingTop: 8,
            }}
          >
            {showOtherError && (
              <div css={{ marginBottom: 8 }}>
                <ErrorAlert>
                  <span>{translate("OTHER_LANGUAGE_ERROR")}</span>
                </ErrorAlert>
              </div>
            )}

            <div css={{ position: "relative" }}>
              <input
                type="text"
                aria-label="Other Language"
                value={otherLanguageText}
                onChange={(e) => {
                  setOtherLanguageText(e.target.value || "");
                  // Clear error when user starts typing
                  if (showOtherError && e.target.value?.trim()) {
                    setShowOtherError(false);
                  }
                }}
                css={{
                  border: "none",
                  borderBottom: `1px solid ${Colors.slate300}`,
                  padding: "8px 0",
                  fontSize: 16,
                  outline: "none",
                  width: "100%",
                  background: "transparent",
                  fontFamily: "inherit",
                  color: "#1e293b", // slate-800 equivalent
                  "&:focus": {
                    borderBottom: `2px solid ${Colors.blue500}`,
                  },
                }}
              />
              {/* Custom placeholder with red asterisk */}
              {!otherLanguageText && (
                <div
                  css={{
                    position: "absolute",
                    left: 0,
                    top: 8,
                    pointerEvents: "none",
                    display: "flex",
                    gap: 2,
                    fontSize: 16,
                  }}
                >
                  <span css={{ color: Colors.slate400 }}>Type language here</span>
                  <span css={{ color: Colors.red500 }}>*</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <SelectItem
      id={modalId}
      label={translate("LABEL_BILINGUAL_REQUIREMENT")}
      value={languageValues.join(", ") || translate("NO_LANGUAGE_REQUIREMENT")}
      stacked
      isDisabled={isDisabled}
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
    >
      <IonModal
        trigger={modalId}
        ref={languageModal}
        aria-label="Language Selection"
        backdropDismiss
        onDidDismiss={handleModalClose}
        className="modal-600 stackedModal"
      >
        <ModalHeader
          title={translate("BILINGUAL_REQUIREMENT_MODAL_HEADER")}
          onModalDismiss={handleModalClose}
          onModalConfirm={handleModalConfirm}
          onIonInput={(e) => setSearchTerm(e?.target?.value || "")}
        />

        {/* Modal Content */}
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-lg)",
            padding: "var(--spacing-2xl) var(--spacing-lg) var(--spacing-lg)",
            overflow: "auto",
            height: "100%",
            backgroundColor: Colors.white300,
          }}
        >
          {/* Description text */}
          <div
            css={{
              fontSize: 16,
              fontWeight: 600,
              color: "#1e293b",
              paddingBottom: 8,
            }}
          >
            {translate("BILINGUAL_REQUIREMENT_DESCRIPTION")}
          </div>
          <Card>
            {/* When loading the Languages data, show a loading spinner */}
            {languagesData === undefined && (
              <div css={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                <IonSpinner name="dots" />
              </div>
            )}

            {/* When there are Languages, show the Languages */}
            {filteredLanguages && filteredLanguages.length > 0 && (
              <>
                {filteredLanguages.map((language: Language, index) => {
                  const isLast = index === filteredLanguages.length - 1;
                  const isOther = language.value === "Other";

                  if (isOther) {
                    return <div key={language.value}>{renderOtherOption(language, !isLast)}</div>;
                  }

                  return (
                    <IonCheckbox
                      key={language.value}
                      labelPlacement="end"
                      justify="start"
                      value={language.value}
                      checked={tempLanguageValues.includes(language.value)}
                      onIonChange={(e) => handleChange(e.detail)}
                      css={{
                        width: "100%",
                        padding: 16,
                        // Always include boxShadow key for readability
                        boxShadow: isLast ? "none" : `inset 0 -1px 0 ${Colors.slate200}`,
                      }}
                    >
                      {language.label}
                    </IonCheckbox>
                  );
                })}
              </>
            )}

            {/* When there are no Languages from search, show Other option */}
            {filteredLanguages &&
              filteredLanguages.length === 0 &&
              languagesData &&
              (() => {
                const otherLanguage = languagesData.find((lang) => lang.value === "Other");
                return otherLanguage ? renderOtherOption(otherLanguage, false) : null;
              })()}
          </Card>
        </div>
      </IonModal>
    </SelectItem>
  );
}
